import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { format, isToday, isYesterday, formatDistanceToNow } from 'date-fns';
import { messageService, Message, SendMessageResponse } from '../../services/message.service';
import { getCurrentAyreneUserId } from '../../services/ayrene.service';
import { userIdMappingService } from '../../services/userIdMapping.service';
import MessageInput from './MessageInput';
import { toast } from 'react-toastify';
import './MessageThread.css';

interface MessageThreadProps {
  receiverId: string;
  receiverName: string;
  receiverAvatar: string;
  receiverEmail: string;
}

const POLLING_INTERVAL = 5000;

const MessageThread: React.FC<MessageThreadProps> = ({ receiverId, receiverName, receiverAvatar, receiverEmail }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(getCurrentAyreneUserId());
  const isNearBottomRef = useRef<boolean>(true);
  const [receiverAyreneId, setReceiverAyreneId] = useState<string | null>(null);
  const [isResolvingId, setIsResolvingId] = useState(true);
  const [idResolutionError, setIdResolutionError] = useState<string | null>(null);
  const [currentAuthToken, setCurrentAuthToken] = useState<string | null>(localStorage.getItem('authToken'));

  const scrollToBottom = (smooth: boolean = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }
  };

  const checkIfNearBottom = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const threshold = 100;
      const isNear = scrollHeight - scrollTop - clientHeight < threshold;
      isNearBottomRef.current = isNear;
      return isNear;
    }
    return true;
  };

  useEffect(() => {
    const handleAuthChange = () => {
      const newAuthToken = localStorage.getItem('authToken');

      if (newAuthToken !== currentAuthToken) {
        console.log('🔄 Account switch detected in MessageThread, resetting state');

        setCurrentAuthToken(newAuthToken);

        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setMessages([]);
        setIsInitialLoading(true);
        setError(null);
        setReceiverAyreneId(null);
        setIsResolvingId(true);
        setIdResolutionError(null);

        const updatedUserId = getCurrentAyreneUserId();
        setCurrentUserId(updatedUserId);
        console.log('🔄 Current user ID updated after account switch:', updatedUserId);
      }
    };

    const checkAuthOnInterval = setInterval(() => {
      handleAuthChange();
    }, 1000);

    return () => {
      clearInterval(checkAuthOnInterval);
    };
  }, [currentAuthToken]);

  useEffect(() => {
    setMessages([]);
    setIsInitialLoading(true);
    setError(null);
    setReceiverAyreneId(null);
    setIsResolvingId(true);
    setIdResolutionError(null);

    const updatedUserId = getCurrentAyreneUserId();
    setCurrentUserId(updatedUserId);
    console.log('🔄 Current user ID updated:', updatedUserId);

    const resolveAyreneUserId = async () => {
      console.log('🔄 Resolving Ayrene user ID for:', receiverEmail);
      setIsResolvingId(true);
      setIdResolutionError(null);

      try {
        const ayreneId = await userIdMappingService.getAyreneUserId(receiverId, receiverEmail);

        if (ayreneId) {
          console.log('✅ Resolved Ayrene user ID:', ayreneId);
          setReceiverAyreneId(ayreneId);
          setIdResolutionError(null);
        } else {
          console.error('❌ Could not resolve Ayrene user ID for:', receiverEmail);
          setIdResolutionError(`${receiverName} is not registered in the messaging system yet.`);
        }
      } catch (error) {
        console.error('❌ Error resolving Ayrene user ID:', error);
        setIdResolutionError('Failed to connect to messaging system. Please try again.');
      } finally {
        setIsResolvingId(false);
      }
    };

    resolveAyreneUserId();
  }, [receiverId, receiverEmail, receiverName]);

  const fetchMessages = useCallback(async (shouldScrollToBottom: boolean = false) => {
    if (!receiverAyreneId || !currentUserId) {
      console.warn('⚠️ Cannot fetch messages: User IDs not resolved yet');
      return;
    }

    try {
      const response = await messageService.getConversation(receiverAyreneId);

      if (response.success && response.messages) {
        setMessages(prevMessages => messageService.mergeMessages(prevMessages, response.messages));
        setError(null);

        if (shouldScrollToBottom || isNearBottomRef.current) {
          setTimeout(() => scrollToBottom(true), 100);
        }
      } else {
        setMessages([]);
      }
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load messages');
      } else {
        setMessages([]);
      }
    } finally {
      setIsInitialLoading(false);
    }
  }, [receiverAyreneId, currentUserId]);

  useEffect(() => {
    if (!receiverAyreneId || isResolvingId) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    setIsInitialLoading(true);
    setError(null);
    fetchMessages(true);

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      fetchMessages(false);
    }, POLLING_INTERVAL);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else {
        if (!pollingIntervalRef.current) {
          fetchMessages(false);
          pollingIntervalRef.current = setInterval(() => {
            fetchMessages(false);
          }, POLLING_INTERVAL);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [receiverAyreneId, isResolvingId, fetchMessages]);

  const handleSendMessage = async (content: string) => {
    if (!receiverAyreneId) {
      toast.error('Cannot send message: User not found in messaging system');
      return;
    }

    try {
      console.log('📤 Sending message to Ayrene user ID:', receiverAyreneId);
      const response: SendMessageResponse = await messageService.sendMessage(receiverAyreneId, content);

      const newMessage: Message = response.message;
      setMessages(prev => messageService.mergeMessages(prev, [newMessage]));

      setTimeout(() => scrollToBottom(true), 100);

      toast.success('Message sent successfully');
    } catch (err: any) {
      console.error('Failed to send message:', err);
      toast.error(err.message || 'Failed to send message');
      throw err;
    }
  };

  const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);

    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'MMM d, h:mm a');
    }
  };

  const getRelativeTime = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '';
    }
  };

  const getSenderId = (message: Message): string => {
    if (typeof message.sender === 'string') {
      return message.sender;
    }
    return message.sender._id;
  };

  if (isResolvingId) {
    return (
      <div className="message-thread-loading">
        <Loader2 size={48} className="spinner" />
        <p>Looking up user...</p>
      </div>
    );
  }

  if (idResolutionError) {
    return (
      <div className="message-thread-error">
        <AlertCircle size={48} />
        <p>{idResolutionError}</p>
        <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
          They may need to create an account in the messaging system first.
        </p>
      </div>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="message-thread-loading">
        <Loader2 size={48} className="spinner" />
        <p>Loading messages...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="message-thread-error">
        <AlertCircle size={48} />
        <p>{error}</p>
        <button onClick={() => fetchMessages(true)} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="message-thread-container">
      <div
        className="messages-list"
        ref={messagesContainerRef}
        onScroll={checkIfNearBottom}
      >
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwnMessage = getSenderId(message) === currentUserId;
            const messageContent = message.content || message.text || '';

            return (
              <div
                key={message._id}
                className={`message-bubble ${isOwnMessage ? 'own-message' : 'other-message'}`}
              >
                {!isOwnMessage && (
                  <div className="message-avatar">
                    <img src={receiverAvatar} alt={receiverName} />
                  </div>
                )}
                <div className="message-content-wrapper">
                  {!isOwnMessage && (
                    <div className="message-sender-name">{receiverName}</div>
                  )}
                  <div className="message-content">
                    {messageContent}
                  </div>
                  <div className="message-metadata">
                    <span className="message-time" title={getRelativeTime(message.createdAt)}>
                      {formatMessageTime(message.createdAt)}
                    </span>
                    {isOwnMessage && (
                      <span className={`message-status status-${message.status}`}>
                        {message.status === 'sent' && '✓'}
                        {message.status === 'delivered' && '✓✓'}
                        {message.status === 'read' && '✓✓'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <MessageInput onSendMessage={handleSendMessage} />
    </div>
  );
};

export default MessageThread;
